import { Css } from "~generated/css";
import { Maybe } from "~generated/graphqlTypes";
import { useLocalStorage, useTestIds } from "~utils";
import {
  VersionBadge_CheckoutConfigFragment,
  VersionBadge_ProductOfferingFragment,
  VersionBadge_VersionFragment,
} from "./VersionBadge.generated";

const SHOW_OFFERING_VERSION_BADGE_KEY = "configurator_showOfferingVersionBadge";

/** Checkout-tester preference, set from the debug modal, that keeps the badge up even when in sync. */
export function useShowOfferingVersionBadge() {
  return useLocalStorage(SHOW_OFFERING_VERSION_BADGE_KEY, false);
}

const badgeStyles = Css.t10.fw500.purple600.wsnw.pPx(4).ba.bcPurple600.br8.$;

type VersionBadgeProps = {
  checkoutConfig: VersionBadge_CheckoutConfigFragment;
};

/**
 * The header's version badge: shown whenever a config is pinned ahead of what checkout serves, plus any
 * time a tester has opted into keeping it up from the debug modal.
 *
 * Gated on `pricingTraceUrl` that is only hooked returned for `Checkout Tester` user group.
 */
export function VersionBadge(props: VersionBadgeProps) {
  const { checkoutConfig } = props;
  const [showWhenInSync] = useShowOfferingVersionBadge();
  const tid = useTestIds(props, "versionBadge");
  const { productOffering: pof, aduProductOffering: aduPof } = checkoutConfig;

  if (!checkoutConfig.pricingTraceUrl) return null;

  // Either offering being ahead of its promoted version counts as previewing
  const isPreviewing =
    isAheadOfCheckout(checkoutConfig.aggregateVersion, pof) ||
    isAheadOfCheckout(checkoutConfig.aduAggregateVersion, aduPof);
  if (!isPreviewing && !showWhenInSync) return null;

  return <OfferingVersionBadge checkoutConfig={checkoutConfig} {...tid} />;
}

function isAheadOfCheckout(
  configured: Maybe<VersionBadge_VersionFragment>,
  pof: Maybe<VersionBadge_ProductOfferingFragment>,
) {
  return (configured?.version ?? 0) > (pof?.aggregateCheckout?.version ?? 0);
}

/**
 * The three POF versions that matter for a config: what the ccfg is pinned to, what checkout currently
 * serves, and what's newest upstream (draft or published). Renders unconditionally — `VersionBadge` owns
 * whether it earns its space.
 *
 * Stacked onto two lines rather than one, because the header's centered step nav leaves ~60px of
 * horizontal slack at 1440px and the single-line form needs ~260px.
 */
export function OfferingVersionBadge(props: VersionBadgeProps) {
  const { checkoutConfig } = props;
  const tid = useTestIds(props, "offeringVersionBadge");
  const { productOffering: pof, aduProductOffering: aduPof } = checkoutConfig;

  if (!pof) return null;

  return (
    // Tighter leading than the t10 default, so the two-per-POF rows still clear the 64px header
    <div css={{ ...badgeStyles, ...Css.df.fdc.gapPx(3).lhPx(12).$ }} {...tid}>
      <VersionBlock
        // Only label the blocks when there are two of them to tell apart
        prefix={aduPof ? "MH" : undefined}
        configured={checkoutConfig.aggregateVersion}
        pof={pof}
        {...tid.mh}
      />
      {aduPof && (
        <VersionBlock prefix="ADU" configured={checkoutConfig.aduAggregateVersion} pof={aduPof} {...tid.adu} />
      )}
    </div>
  );
}

type VersionBlockProps = {
  prefix?: string;
  /** The version the ccfg is pinned to, for either the main house or the ADU. */
  configured: Maybe<VersionBadge_VersionFragment>;
  /** The matching POF, i.e. `aggregateCheckout`/`aggregateLatest` are always this POF's own. */
  pof: VersionBadge_ProductOfferingFragment;
};

function VersionBlock(props: VersionBlockProps) {
  const { prefix, configured, pof } = props;
  const tid = useTestIds(props, "versionBlock");

  const configuredVersion = configured?.version;
  const promotedVersion = pof.aggregateCheckout?.version;
  // A ccfg is "previewing" when it's pinned to anything other than what checkout serves
  const isPreviewing = !!configuredVersion && !!promotedVersion && configuredVersion !== promotedVersion;

  return (
    <div css={Css.df.aic.gapPx(4).$} {...tid}>
      {prefix && <span css={Css.fw700.purple400.wPx(26).$}>{prefix}</span>}
      <div css={Css.df.fdc.$}>
        <VersionPart
          label="configuration"
          version={configuredVersion}
          highlight={isPreviewing}
          {...tid.configuration}
        />
        <div css={Css.df.aic.gapPx(4).$}>
          <VersionPart label="promoted" version={promotedVersion} {...tid.promoted} />
          <Separator />
          <VersionPart label="latest" version={pof.aggregateLatest.version} {...tid.latest} />
        </div>
      </div>
    </div>
  );
}

type VersionPartProps = {
  label: string;
  version: Maybe<number>;
  /** Set on the configured version when it differs from what checkout serves. */
  highlight?: boolean;
};

function VersionPart(props: VersionPartProps) {
  const { label, version, highlight = false } = props;
  const tid = useTestIds(props, label);
  return (
    <span css={Css.df.aic.gapPx(3).$} {...tid}>
      <span css={Css.purple400.$}>{label}:</span>
      <span css={Css.fw600.purple600.if(highlight).fw700.purple800.$}>{version ? `v${version}` : "—"}</span>
    </span>
  );
}

function Separator() {
  return <span css={Css.purple200.$}>|</span>;
}
